import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Play, Square, Bookmark } from 'lucide-react';
import { getAllBookmarks, removeBookmark } from '../utils/bookmarkStorage';
import { DATA_BASE_URL } from '../config.js';
import './Bookmarks.css';

const Bookmarks = () => {
    const navigate = useNavigate();
    const [bookmarks, setBookmarks] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentAudioPath, setCurrentAudioPath] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        loadBookmarks();
    }, []);

    const loadBookmarks = () => {
        const allBookmarks = getAllBookmarks();
        setBookmarks(allBookmarks);
    };

    const handleRemoveBookmark = (contentId, subtitleIndex) => {
        if (window.confirm('이 북마크를 삭제하시겠습니까?')) {
            if (removeBookmark(contentId, subtitleIndex)) {
                loadBookmarks(); // 목록 새로고침
            }
        }
    };

    const handleGoToContent = (contentId, accessKey, subtitleIndex) => {
        navigate(`/viewer/${contentId}/${accessKey}#subtitle-${subtitleIndex}`);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const playAudio = async (audioPath) => {
        if (audioPath && audioRef.current) {
            try {
                const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
                audioRef.current.src = `${DATA_BASE_URL}${audioPath}?token=${encodeURIComponent(token)}`;
                audioRef.current.loop = true; // 무한반복 설정

                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => reject(new Error('Audio load timeout')), 5000);
                    audioRef.current.onloadeddata = () => {
                        clearTimeout(timeout);
                        resolve();
                    };
                    audioRef.current.onerror = () => {
                        clearTimeout(timeout);
                        reject(new Error('Audio load failed'));
                    };
                    audioRef.current.load();
                });

                await audioRef.current.play();
                setIsPlaying(true);
                setCurrentAudioPath(audioPath);
            } catch (error) {
                console.error('Audio play error:', error);
                alert('오디오 재생에 실패했습니다.');
                setIsPlaying(false);
                setCurrentAudioPath(null);
            }
        }
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentAudioPath(null);
    };

    const toggleAudio = (audioPath) => {
        if (isPlaying && currentAudioPath === audioPath) {
            stopAudio();
        } else {
            playAudio(audioPath);
        }
    };

    return (
        <div className="bookmarks-page">
            <header className="bookmarks-header">
                <div className="header-content">
                    <button
                        className="back-button"
                        onClick={() => navigate(-1)}
                        title="뒤로 가기"
                        aria-label="뒤로 가기"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div className="title-wrapper">
                        <Bookmark size={22} className="title-icon" />
                        <h1>나의 북마크</h1>
                    </div>
                </div>
            </header>

            <div className="bookmarks-content">
                {bookmarks.length === 0 ? (
                    <div className="no-bookmarks">
                        <p>저장된 북마크가 없습니다.</p>
                        <button onClick={() => navigate('/')} className="go-home-button">
                            컨텐츠 목록으로 가기
                        </button>
                    </div>
                ) : (
                    <div className="bookmarks-list">
                        {bookmarks.map((bookmark) => (
                            <div key={bookmark.id} className="bookmark-item">
                                <div className="bookmark-header">
                                    <h3
                                        className="content-title"
                                        onClick={() => handleGoToContent(bookmark.contentId, bookmark.accessKey, bookmark.subtitleIndex)}
                                    >
                                        {bookmark.contentTitle}
                                    </h3>
                                </div>

                                <div className="bookmark-time">
                                    {formatTime(bookmark.subtitle.startTime)} - {formatTime(bookmark.subtitle.endTime)}
                                </div>

                                <div className="bookmark-text">
                                    <div className="english">{bookmark.subtitle.english}</div>
                                    <div className="korean">{bookmark.subtitle.korean}</div>
                                    {bookmark.subtitle.comment && (
                                        <div className="comment">{bookmark.subtitle.comment}</div>
                                    )}
                                </div>

                                {bookmark.subtitle.words && bookmark.subtitle.words.length > 0 && (
                                    <div className="words-section">
                                        <h4>주요 단어</h4>
                                        <div className="words-list">
                                            {bookmark.subtitle.words.map((wordObj, idx) => (
                                                <div key={idx} className="word-item">
                                                    <span className="word">{wordObj.word}</span>
                                                    <span className="meaning">{wordObj.meaning}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="bookmark-actions">
                                    {bookmark.subtitle.audioPath && (
                                        <button
                                            className={`audio-button ${isPlaying && currentAudioPath === bookmark.subtitle.audioPath ? 'playing' : ''}`}
                                            onClick={() => toggleAudio(bookmark.subtitle.audioPath)}
                                            title={isPlaying && currentAudioPath === bookmark.subtitle.audioPath ? "오디오 정지" : "오디오 재생"}
                                        >
                                            {isPlaying && currentAudioPath === bookmark.subtitle.audioPath ? (
                                                <>
                                                    <Square size={16} />
                                                    <span>정지</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Play size={16} />
                                                    <span>재생</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                    <button
                                        className="view-button"
                                        onClick={() => handleGoToContent(bookmark.contentId, bookmark.accessKey, bookmark.subtitleIndex)}
                                    >
                                        컨텐츠 보기
                                    </button>
                                    <button
                                        className="delete-button"
                                        onClick={() => handleRemoveBookmark(bookmark.contentId, bookmark.subtitleIndex)}
                                        title="북마크 삭제"
                                    >
                                        <Trash2 size={18} />
                                        <span>삭제</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <audio
                ref={audioRef}
                controls
                style={{ display: 'none' }}
                preload="metadata"
                onError={(e) => {
                    console.error('Audio error:', e);
                    setIsPlaying(false);
                    setCurrentAudioPath(null);
                }}
                onEnded={() => {
                    // 무한반복이므로 onEnded 이벤트는 발생하지 않음
                    // 하지만 혹시 모를 상황을 위해 유지
                    setIsPlaying(false);
                    setCurrentAudioPath(null);
                }}
                onPause={() => {
                    if (audioRef.current && audioRef.current.currentTime === 0) {
                        setIsPlaying(false);
                    }
                }}
                onPlay={() => {
                    setIsPlaying(true);
                }}
            />
        </div>
    );
};

export default Bookmarks;
